import React, { useState } from 'react';
import { Button } from 'src/components/ui/Button';
import type { OrgCustomRole } from 'src/types/org.types';

interface EditMemberModalProps {
  isDemo: boolean;
  orgId: string | undefined;
  member: any;
  customRoles: OrgCustomRole[];
  onClose: () => void;
  onUpdateRole: (userId: string, roleId: any, customRoleId: string | undefined) => Promise<void>;
  onRemoveMember: (userId: string) => Promise<void>;
}

export const EditMemberModal: React.FC<EditMemberModalProps> = ({
  isDemo,
  orgId,
  member,
  customRoles,
  onClose,
  onUpdateRole,
  onRemoveMember,
}) => {
  // Determine if member currently has a custom role
  const hasCustomRole = member.customRoleId != null;

  const [useCustomRole, setUseCustomRole] = useState(hasCustomRole);
  const [formData, setFormData] = useState({
    role: member.role,
    customRoleId: member.customRoleId || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const handleUpdateRole = async () => {
    if (!orgId) return;

    // Validate custom role selection
    if (useCustomRole && !formData.customRoleId) {
      setModalError('Please select a custom role');
      return;
    }

    setIsSaving(true);
    setModalError(null);

    try {
      await onUpdateRole(
        member.userId,
        useCustomRole ? undefined : formData.role,
        useCustomRole ? formData.customRoleId : undefined
      );
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update member';
      setModalError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!orgId) return;

    setIsRemoving(true);
    setModalError(null);

    try {
      await onRemoveMember(member.userId);
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove member';
      setModalError(errorMessage);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleClose = () => {
    setModalError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-900 border border-white/10 rounded-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white text-xl font-semibold">Edit Member</h2>
          <button onClick={handleClose} className="text-white/40 hover:text-white">✕</button>
        </div>

        {modalError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {modalError}
          </div>
        )}

        {!showRemoveConfirm ? (
          <>
            <div className="mb-4 pb-4 border-b border-white/10">
              <p className="text-white font-medium">{member.displayName}</p>
              <p className="text-white/40 text-sm">{member.email}</p>
            </div>

            <div className="space-y-4">
              {/* Role type selector */}
              <div>
                <label className="block text-white text-sm font-medium mb-3">Role Type</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setUseCustomRole(false)}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !useCustomRole
                        ? 'bg-white/10 border border-white/20 text-white'
                        : 'border border-white/10 text-white/60 hover:bg-white/5'
                    }`}
                  >
                    Standard Role
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseCustomRole(true)}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      useCustomRole
                        ? 'bg-white/10 border border-white/20 text-white'
                        : 'border border-white/10 text-white/60 hover:bg-white/5'
                    }`}
                  >
                    Custom Role
                  </button>
                </div>
              </div>

              {/* Role selector */}
              {!useCustomRole ? (
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Standard Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white/30"
                  >
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Custom Role</label>
                  <select
                    value={formData.customRoleId}
                    onChange={(e) => setFormData(prev => ({ ...prev, customRoleId: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white/30"
                  >
                    <option value="">Select a custom role</option>
                    {customRoles.map(role => (
                      <option key={role.customRoleId} value={role.customRoleId}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 mt-6">
              <div className="flex gap-3">
                <Button onClick={handleClose} variant="ghost" className="flex-1">Cancel</Button>
                <Button
                  onClick={handleUpdateRole}
                  variant="default"
                  className="flex-1"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Update Role'}
                </Button>
              </div>
              <Button
                onClick={() => setShowRemoveConfirm(true)}
                variant="destructive"
                className="w-full"
                disabled={isSaving || isRemoving}
              >
                Remove Member
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-6">
              <p className="text-white mb-2">
                Are you sure you want to remove <strong>{member.displayName}</strong> from this organization?
              </p>
              <p className="text-white/40 text-sm">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setShowRemoveConfirm(false)}
                variant="ghost"
                className="flex-1"
                disabled={isRemoving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRemoveMember}
                variant="destructive"
                className="flex-1"
                disabled={isRemoving}
              >
                {isRemoving ? 'Removing...' : 'Remove Member'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
